import { useState, useEffect } from "react";
import TopAppBar from "../global/TopBar";
import SideBar from "../global/SideBar";
import {
  Box,
  Typography,
  useTheme,
  Grid,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
  Tooltip,
  Button,
  Card,
  CardContent,
  IconButton,
  alpha,
  Container,
  Stack,
} from "@mui/material";
import HeaderPage from "../../components/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import FlagIcon from "@mui/icons-material/Flag";
import VerifiedIcon from "@mui/icons-material/Verified";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LoginIcon from "@mui/icons-material/Login";
import InventoryIcon from '@mui/icons-material/Inventory';
import SchoolIcon from '@mui/icons-material/School';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PaymentsIcon from '@mui/icons-material/Payments';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';

const Admin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const { getData, userRole, userID } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [stats, setStats] = useState({
    totalStaff: 0,
    totalStudents: 0,
    totalParents: 0,
    totalNextOfKin: 0,
    totalInventory: 0,
    totalBorrow: 0,
    totalPayments: 0,
    totalRegistrations: 0,
    totalLeaves: 0,
    pendingLeaves: 0,
    approvedLeaves: 0,
    activeStaff: 0,
    verifiedStaff: 0,
    emailVerified: 0,
    loggedInToday: 0,
    newStaffThisMonth: 0
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch all dashboard data
  const fetchDashboardData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const endpoints = [
        { name: 'staffs', key: 'totalStaff' },
        { name: 'students_V2', key: 'totalStudents' },
        { name: 'parent_V2', key: 'totalParents' },
        { name: 'next_of_keen_V2', key: 'totalNextOfKin' },
        { name: 'inventory', key: 'totalInventory' },
        { name: 'borrow_V2', key: 'totalBorrow' },
        { name: 'payment_V2', key: 'totalPayments' },
        { name: 'registration_V2', key: 'totalRegistrations' },
        { name: 'leaves_V2', key: 'totalLeaves' }
      ];

      const statsData = { ...stats };
      const currentDate = new Date();
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

      for (const endpoint of endpoints) {
        try {
          const response = await getData('proccess.php', endpoint.name);
          if (response.success) {
            statsData[endpoint.key] = response.data.length;
            
            // Leaves specific calculations
            if (endpoint.name === 'leaves_V2') {
              const pending = response.data.filter(l => l.status === 0).length;
              const approved = response.data.filter(l => l.status === 4).length;
              statsData.pendingLeaves = pending;
              statsData.approvedLeaves = approved;
              
              const filteredLeaves = response.data.filter(l => l.status === 4);
              setLeaves(filteredLeaves);
            }
            
            // Staff specific calculations
            if (endpoint.name === 'staffs') {
              setStaffs(response.data);
              
              const active = response.data.filter(s => s.status === "active" || s.status === 1).length;
              const verified = response.data.filter(s => s.isVerified === 1 || s.isVerified === true).length;
              const emailVerified = response.data.filter(s => s.email_verified === 1 || s.email_verified === true).length;
              const newThisMonth = response.data.filter(s => {
                if (s.createDate) {
                  const createDate = new Date(s.createDate);
                  return createDate >= firstDayOfMonth;
                }
                return false;
              }).length;
              
              const loggedInToday = response.data.filter(s => {
                if (s.last_login) {
                  const lastLogin = new Date(s.last_login);
                  const today = new Date();
                  return lastLogin.toDateString() === today.toDateString();
                }
                return false;
              }).length;

              statsData.activeStaff = active;
              statsData.verifiedStaff = verified;
              statsData.emailVerified = emailVerified;
              statsData.loggedInToday = loggedInToday;
              statsData.newStaffThisMonth = newThisMonth;

              const currentUserData = response.data.find(u => u.Id === userID);
              if (currentUserData) setCurrentUser(currentUserData);
            }
          }
        } catch (err) {
          console.error(`Error fetching ${endpoint.name}:`, err);
        }
      }
      
      setStats(statsData);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
      if (showRefresh) setTimeout(() => setRefreshing(false), 1000);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [getData, userID]);

  // Redirect receptionist
  useEffect(() => {
    if (userRole === "receptionist") {
      navigate("/website/dashboard");
    }
  }, [userRole, navigate]);

  // Format date and time
  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // Staff grouping by role
  const staffByRole = staffs.reduce((acc, staff) => {
    const role = staff.role || 'Unassigned';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  const onlineUsers = staffs.filter(s => {
    if (s.last_login) {
      const lastLogin = new Date(s.last_login);
      const today = new Date();
      return lastLogin.toDateString() === today.toDateString();
    }
    return false;
  });

  // Dashboard stat cards configuration
  const statCards = [
    { 
      title: 'Total Staff', 
      value: stats.totalStaff, 
      icon: <PeopleIcon />, 
      color: colors.blue[500],
      bgColor: alpha(colors.blue[500], 0.1),
      trend: '+12%',
      trendUp: true,
      path: '/admin/dashboard/staffs'
    },
    { 
      title: 'Total Students', 
      value: stats.totalStudents, 
      icon: <SchoolIcon />, 
      color: colors.green[500],
      bgColor: alpha(colors.green[500], 0.1),
      trend: '+5%',
      trendUp: true,
      path: '/admin/dashboard/students'
    },
    { 
      title: 'Parents', 
      value: stats.totalParents, 
      icon: <FamilyRestroomIcon />, 
      color: colors.indigo[500],
      bgColor: alpha(colors.indigo[500], 0.1),
      trend: '+8%',
      trendUp: true,
      path: '/admin/dashboard/parentsguardians'
    },
    { 
      title: 'Next of Kin', 
      value: stats.totalNextOfKin, 
      icon: <GroupIcon />, 
      color: colors.orange[500],
      bgColor: alpha(colors.orange[500], 0.1),
      trend: '+3%',
      trendUp: true,
      path: '/admin/dashboard/nextofkin'
    },
    { 
      title: 'Inventory', 
      value: stats.totalInventory, 
      icon: <InventoryIcon />, 
      color: colors.teal[500],
      bgColor: alpha(colors.teal[500], 0.1),
      trend: '+15%',
      trendUp: true,
      path: '/admin/dashboard/inventory'
    },
    { 
      title: 'Borrow Records', 
      value: stats.totalBorrow, 
      icon: <AssignmentIcon />, 
      color: colors.red[500],
      bgColor: alpha(colors.red[500], 0.1),
      trend: '-2%',
      trendUp: false,
      path: '/admin/dashboard/borrow'
    },
    { 
      title: 'Payments', 
      value: stats.totalPayments, 
      icon: <PaymentsIcon />, 
      color: colors.newTeal[500],
      bgColor: alpha(colors.newTeal[500], 0.1),
      trend: '+25%',
      trendUp: true,
      path: '/admin/dashboard/payments'
    },
    { 
      title: 'Registrations', 
      value: stats.totalRegistrations, 
      icon: <HowToRegIcon />, 
      color: colors.yellow[500],
      bgColor: alpha(colors.yellow[500], 0.1),
      trend: '+10%',
      trendUp: true,
      path: '/admin/dashboard/registrations'
    },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: colors.dark[900] }}>
      <SideBar />
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <TopAppBar />
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header Section with Refresh */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box>
                <Typography variant="h2" color={colors.green[400]}>Admin Dashboard</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 1.5, 
                    bgcolor: colors.primary[500],
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarTodayIcon sx={{ color: colors.blue[500], fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: colors.primary[100] }}>
                      {formattedDate}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ bgcolor: colors.gray[500] }} />
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccessTimeIcon sx={{ color: colors.orange[500], fontSize: 20 }} />
                    <Typography variant="h6" sx={{ color: colors.orange[500], fontWeight: 'bold' }}>
                      {formattedTime}
                    </Typography>
                  </Box>
                </Paper>
                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={() => fetchDashboardData(true)}
                    sx={{ 
                      bgcolor: colors.primary[500],
                      '&:hover': { bgcolor: colors.primary[100], color: colors.primary[900] }
                    }}
                  >
                    <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Welcome Banner */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 4,
                background: `linear-gradient(135deg, ${colors.blue[500]} 0%, ${colors.blue[600]} 100%)`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Typography variant="h3" fontWeight="bold" color={colors.white[500]} mb={1}>
                      Welcome, {currentUser?.FirstName || 'Admin'}! 👋, How are you doing today?
                    </Typography>
                    <Typography variant="h6" color={colors.white[500]} sx={{ opacity: 0.9 }}>
                      Here's what's happening with in your organization today.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: alpha(colors.white[500], 0.1),
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        border: `1px solid ${alpha(colors.white[500], 0.2)}`
                      }}
                    >
                      <Typography variant="body2" color={colors.white[500]} sx={{ opacity: 0.8 }} gutterBottom>
                        Quick Stats
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography component="span" color={colors.white[500]}>Active Staff</Typography>
                        <Typography component="span" color={colors.white[500]} fontWeight="bold">{stats.activeStaff}/{stats.totalStaff}</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.totalStaff ? (stats.activeStaff / stats.totalStaff) * 100 : 0}
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: alpha(colors.white[500], 0.2),
                          '& .MuiLinearProgress-bar': { 
                            bgcolor: colors.white[500],
                            borderRadius: 4
                          }
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Stat Cards Grid */}
            <Grid container spacing={3} mb={4}>
              {statCards.map((card, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <Card 
                    onClick={() => navigate(card.path)}
                    sx={{ 
                      bgcolor: colors.primary[500],
                      border: `1px solid ${colors.primary[400]}`,
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${alpha(card.color, 0.2)}`,
                        borderColor: card.color
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box 
                          sx={{ 
                            p: 1.5,
                            bgcolor: card.bgColor,
                            borderRadius: 2,
                            color: card.color
                          }}
                        >
                          {card.icon}
                        </Box>
                        <Chip 
                          label={card.trend}
                          size="small"
                          icon={card.trendUp ? <TrendingUpIcon /> : <TrendingDownIcon />}
                          sx={{ 
                            bgcolor: card.trendUp ? alpha(colors.green[500], 0.1) : alpha(colors.red[500], 0.1),
                            color: card.trendUp ? colors.green[500] : colors.red[500],
                            '& .MuiChip-icon': { color: 'inherit' }
                          }}
                        />
                      </Box>
                      <Typography variant="h3" fontWeight="bold" color={colors.white[200]} mb={1}>
                        {card.value.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color={colors.gray[400]}>
                        {card.title}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Second Row - Detailed Stats */}
            <Grid container spacing={3} mb={4}>
              {/* Staff Verification Stats */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.primary[400]}`,
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color={colors.primary[100]} mb={3}>
                    Staff Overview
                  </Typography>
                  
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>Verified Staff</Typography>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>
                        {stats.verifiedStaff}/{stats.totalStaff}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalStaff ? (stats.verifiedStaff / stats.totalStaff) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: colors.primary[400],
                        '& .MuiLinearProgress-bar': { bgcolor: colors.teal[500] }
                      }}
                    />
                  </Box>

                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>Email Verified</Typography>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>
                        {stats.emailVerified}/{stats.totalStaff}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalStaff ? (stats.emailVerified / stats.totalStaff) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: colors.primary[400],
                        '& .MuiLinearProgress-bar': { bgcolor: colors.blue[500] }
                      }}
                    />
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>Active Staff</Typography>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>
                        {stats.activeStaff}/{stats.totalStaff}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalStaff ? (stats.activeStaff / stats.totalStaff) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: colors.primary[400],
                        '& .MuiLinearProgress-bar': { bgcolor: colors.green[500] }
                      }}
                    />
                  </Box>

                  <Box mt={3} pt={3} borderTop={`1px solid ${colors.primary[400]}`}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color={colors.primary[100]} gutterBottom>
                          New This Month
                        </Typography>
                        <Typography variant="h5" color={colors.green[500]} fontWeight="bold">
                          +{stats.newStaffThisMonth}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color={colors.primary[100]} gutterBottom>
                          Online Now
                        </Typography>
                        <Typography variant="h5" color={colors.blue[500]} fontWeight="bold">
                          {onlineUsers.length}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>

              {/* Leave Statistics */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.primary[400]}`,
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color={colors.primary[100]} mb={3}>
                    Leave Overview
                  </Typography>

                  <Box textAlign="center" mb={3}>
                    <Typography variant="h2" color={colors.orange[500]} fontWeight="bold">
                      {stats.pendingLeaves}
                    </Typography>
                    <Typography variant="body2" color={colors.primary[100]}>
                      Pending Leave Requests
                    </Typography>
                  </Box>

                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>Approved Leaves</Typography>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>{stats.approvedLeaves}</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalLeaves ? (stats.approvedLeaves / stats.totalLeaves) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: colors.primary[400],
                        '& .MuiLinearProgress-bar': { bgcolor: colors.green[500] }
                      }}
                    />
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>Staff on Leave</Typography>
                      <Typography component="span" variant="body2" color={colors.primary[100]}>{leaves.length}</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={stats.totalStaff ? (leaves.length / stats.totalStaff) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: colors.primary[400],
                        '& .MuiLinearProgress-bar': { bgcolor: colors.orange[500] }
                      }}
                    />
                  </Box>

                  <Box mt={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/admin/dashboard/leave')}
                      sx={{ 
                        borderColor: colors.blue[500],
                        color: colors.blue[500],
                        '&:hover': {
                          borderColor: colors.blue[400],
                          bgcolor: alpha(colors.blue[500], 0.1)
                        }
                      }}
                    >
                      Manage Leaves
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              {/* Staff by Role */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.primary[400]}`,
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color={colors.primary[100]} mb={3}>
                    Staff by Role
                  </Typography>

                  <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                    {Object.entries(staffByRole).map(([role, count]) => (
                      <Box key={role} mb={2}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography component="span" variant="body2" color={colors.primary[100]}>{role}</Typography>
                          <Typography component="span" variant="body2" color={colors.primary[100]}>{count}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={stats.totalStaff ? (count / stats.totalStaff) * 100 : 0}
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: colors.primary[400],
                            '& .MuiLinearProgress-bar': { 
                              bgcolor: role === 'admin' ? colors.red[500] :
                                      role === 'hr' ? colors.blue[500] :
                                      role === 'supervisor' ? colors.green[500] :
                                      role === 'receptionist' ? colors.orange[500] :
                                      colors.indigo[500]
                            }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Third Row - Recent Activity */}
            <Grid container spacing={3}>
              {/* Recent Staff */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.primary[400]}`,
                    borderRadius: 3
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color={colors.primary[100]}>
                      Recent Staff Members
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate('/admin/dashboard/staffs')}
                      sx={{ color: colors.blue[500] }}
                    >
                      View All
                    </Button>
                  </Box>

                  <List sx={{ p: 0 }}>
                    {staffs.slice(0, 5).map((staff, index) => (
                      <Box key={staff.Id}>
                        <ListItem sx={{ p: 1.5 }}>
                          <ListItemAvatar>
                            <Avatar 
                              src={staff.image ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}` : ''}
                              sx={{ 
                                width: 48, 
                                height: 48,
                                bgcolor: colors.primary[400],
                                border: `2px solid ${staff.status === 'active' || staff.status === 1 ? colors.green[500] : colors.gray[500]}`
                              }}
                            >
                              {staff.FirstName?.[0]}{staff.LastName?.[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={
                              <Typography variant="body1" fontWeight="bold" color={colors.primary[100]}>
                                {staff.FirstName} {staff.LastName}
                              </Typography>
                            }
                            secondary={
                              <Box component="span" display="flex" alignItems="center" gap={1} mt={0.5}>
                                <Chip 
                                  label={staff.role || 'Staff'} 
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    bgcolor: alpha(colors.blue[500], 0.1),
                                    color: colors.blue[500],
                                    fontSize: '0.7rem'
                                  }}
                                />
                                <Typography component="span" variant="caption" color={colors.primary[100]}>
                                  {staff.department || 'No Dept'}
                                </Typography>
                              </Box>
                            }
                          />
                          <Tooltip title="View Profile">
                            <IconButton 
                              size="small"
                              sx={{ color: colors.primary[100] }}
                              onClick={() => navigate(`/admin/dashboard/staff/${staff.Id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItem>
                        {index < 4 && <Divider sx={{ bgcolor: colors.primary[400] }} />}
                      </Box>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Online Staff */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3,
                    bgcolor: colors.primary[500],
                    border: `1px solid ${colors.primary[400]}`,
                    borderRadius: 3
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight="bold" color={colors.primary[100]}>
                      Online Staff
                    </Typography>
                    <Chip 
                      label={`${onlineUsers.length} online`}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(colors.green[500], 0.1),
                        color: colors.green[500]
                      }}
                    />
                  </Box>

                  {onlineUsers.length > 0 ? (
                    <List sx={{ p: 0 }}>
                      {onlineUsers.slice(0, 5).map((staff, index) => (
                        <Box key={staff.Id}>
                          <ListItem sx={{ p: 1.5 }}>
                            <ListItemAvatar>
                              <Avatar 
                                src={staff.image ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}` : ''}
                                sx={{ width: 48, height: 48, bgcolor: colors.primary[400] }}
                              >
                                {staff.FirstName?.[0]}{staff.LastName?.[0]}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={
                                <Typography variant="body1" fontWeight="bold" color={colors.primary[100]}>
                                  {staff.FirstName} {staff.LastName}
                                </Typography>
                              }
                              secondary={
                                <Box component="span" display="flex" alignItems="center" gap={1} mt={0.5}>
                                  <Box 
                                    component="span"
                                    sx={{ 
                                      width: 8, 
                                      height: 8, 
                                      borderRadius: '50%',
                                      bgcolor: colors.green[500],
                                      display: 'inline-block'
                                    }} 
                                  />
                                  <Typography component="span" variant="caption" color={colors.green[500]}>
                                    Online
                                  </Typography>
                                  <Typography component="span" variant="caption" color={colors.primary[100]}>
                                    • Last active: {new Date(staff.last_login).toLocaleTimeString()}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < onlineUsers.length - 1 && index < 4 && (
                            <Divider sx={{ bgcolor: colors.primary[400] }} />
                          )}
                        </Box>
                      ))}
                    </List>
                  ) : (
                    <Box textAlign="center" py={4}>
                      <Typography color={colors.primary[100]}>No staff online at the moment</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: alpha(colors.dark[900], 0.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <Paper sx={{ p: 3, borderRadius: 2, bgcolor: colors.primary[500] }}>
            <Typography color={colors.white[500]}>Loading dashboard...</Typography>
          </Paper>
        </Box>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Admin;